import { getTimersForJob } from '../../modules/katapult-workflows/WorkflowTimers.js';
import { GetAllowedActionButtons } from '../../modules/PoleApplication.js';
import { KatapultJob } from '../../modules/katapult-job/KatapultJob.js';
import { KatapultModel } from '../../modules/katapult-model/KatapultModel.js';
import { config } from '../../app-configuration/config.js';
import firebase from 'firebase/compat/app';

/**
 * @typedef {Object} WorkflowActionsContext
 * @property {Object[]} actionButtons
 * @property {Timer[]} timers
 * @property {Object} currentStatus
 * @property {Object} nodes
 * @property {Object} connections
 * @property {Object} modelAttributes
 * @property {Object} attachers
 */
export class OverviewActions {
  /**
   * @param {import('firebase/compat/app').default} [firebaseApp]
   */
  constructor(firebaseApp = firebase) {
    /** @type {import('firebase/compat/app').default} */
    this._firebase = firebaseApp;
  }

  /**
   * @param {Object} params
   * @param {string} params.jobId
   * @param {string} params.jobModel
   * @param {Object} params.jobMetadata
   * @param {string} params.userGroup
   * @param {Object} [params.attachers]
   * @returns {Promise<WorkflowActionsContext>}
   */
  async getWorkflowActionsContext({ jobId, jobModel, jobMetadata, userGroup, attachers = null }) {
    if (!jobId) throw new Error('A job id is required to load workflow actions');
    if (!jobModel) throw new Error('A job model is required to load workflow actions');

    const appType = jobMetadata?.app_type;
    if (!appType) throw new Error('The job has no workflow');

    const appStatus = jobMetadata?.app_status;
    const attachmentOwner = jobMetadata?.attachment_owner ?? null;
    const serverUtilityCompany = config.firebaseConfigs[config.appName].utilityCompany ?? null;

    const job = KatapultJob.fromId(jobId, { firebaseApp: this._firebase });
    const model = KatapultModel.fromId(jobModel, { firebaseApp: this._firebase });

    const [nodes, connections, jobOwner, modelAttributes, statuses, isAttachmentsPortalModel, attacherLabels, currentUser] =
      await Promise.all([
        job.getNodes(),
        job.getConnections(),
        job.getOwner(),
        job.getModelAttributes(),
        model.getModelPath(`portal_config/app_types/${appType}/statuses`),
        model.getModelPath('portal_config/is_3pa_portal_model').then((value) => value === true),
        attachmentOwner
          ? model.getModelPath(`portal_config/user_options/attacher_settings/${attachmentOwner}/labels`)
          : Promise.resolve(null),
        this.getCurrentUser(userGroup)
      ]);

    const currentStatus = this.#findStatusByName(statuses, appStatus);

    const attacher = attachmentOwner ? (attachers?.[attachmentOwner] ?? null) : null;

    const workflowOwner = this.#getWorkflowOwner(jobModel, jobOwner, serverUtilityCompany);

    const actionButtons = GetAllowedActionButtons(currentStatus?.action_buttons, {
      userGroup,
      metadata: jobMetadata,
      nodes,
      connections,
      workflowOwner,
      attacher,
      attacherLabels,
      isAttachmentsPortalModel,
      currentUser
    });

    const timers = await getTimersForJob(jobId, { jobModel, userGroup, workflowId: appType });

    return { actionButtons, timers, currentStatus, nodes, connections, modelAttributes, attachers };
  }

  /**
   * @param {string} userGroup
   * @returns {Promise<Object>}
   */
  async getCurrentUser(userGroup) {
    const uid = this._firebase.auth().currentUser?.uid;
    if (!uid || !userGroup) return null;
    const userModel = KatapultModel.fromId(userGroup, { firebaseApp: this._firebase });
    return await userModel.getModelPath(`user_data/${uid}`);
  }

  /**
   * @param {string} jobModel
   * @param {string} jobOwner
   * @param {string} serverUtilityCompany
   * @returns {string}
   */
  #getWorkflowOwner(jobModel, jobOwner, serverUtilityCompany) {
    const isLegacyServerUtilityModel = serverUtilityCompany != null && jobModel === serverUtilityCompany;
    return isLegacyServerUtilityModel ? serverUtilityCompany : (jobOwner ?? null);
  }

  /**
   * @param {Object.<string, {name: string}>} statuses
   * @param {string} statusName
   * @returns {Object}
   */
  #findStatusByName(statuses, statusName) {
    return Object.values(statuses ?? {}).find((status) => status?.name === statusName) ?? null;
  }
}
